import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "@tanstack/react-router";
import BreadcrumbSimple from '../../components/BreadcrumbSimple';
import Modal from '../../components/Modal';
import { toast } from 'react-toastify';
import { handelTitle, getMainWidth } from '../../utils';
import { sessionStorageGet } from '../../lib/window-shim';

/**
 * DeployContainer - Page for deploying/updating NFT collections
 * Receives collection and files data from Management page via navigation state
 */
/**
 * Deploy Collection container component.
 *
 * Handles the deployment of NFT collections after they have been created
 * in the Management page. Receives collection data and files via navigation state.
 * Displays deployment information, metadata preview, and handles the deployment process.
 * Requires authentication and redirects to sign-in if not logged in.
 *
 * @module containers/Deploy
 * @category Containers
 * @returns {React.ReactElement} Rendered deployment page or loading state
 */
const DeployContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [show, setShow] = useState(true);
  const [content, setContent] = useState("");
  const [payload, setPayload] = useState(null);
  const [collectionData, setCollectionData] = useState(null);
  const [filesData, setFilesData] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get payload from navigation state
  const locationState = location.state;
  const receivedPayload = locationState?.payload;

  // Authentication check
  useEffect(() => {
    const token = sessionStorageGet('token');
    if (!token) {
      toast("Log in first");
      navigate({ to: "/signin" });
      return;
    }

    handelTitle('Deploy Collection');
    getMainWidth();

    // Process incoming payload from Management page
    if (receivedPayload && receivedPayload.length === 2) {
      const [collData, files] = receivedPayload;
      setPayload(receivedPayload);
      setCollectionData(collData);
      setFilesData(files);

      // Show upload confirmation
      setContent(
        <div>
          <h6>Ready to Deploy</h6>
          <p>Collection: {collData?.name || 'Unnamed'}</p>
          <p>Files: {files?.length || 0} items</p>
          <div className="mt-3">
            <small className="text-muted">
              This is where deployment logic would be implemented.
              The data is ready for processing.
            </small>
          </div>
        </div>
      );
    } else {
      // No payload - show empty state
      setContent(
        <div>
          <h6>No Data Received</h6>
          <p>Please navigate from the Manage Collections page with files to deploy.</p>
          <Link to="/manage" className="btn btn-primary mt-2">
            Go to Manage
          </Link>
        </div>
      );
    }

    setIsLoading(false);
  }, [receivedPayload, navigate]);

  // Placeholder: fetch metadata (would be implemented with actual API)
  useEffect(() => {
    if (collectionData) {
      // Mock metadata for demonstration
      setMetadata({
        name: collectionData.name || 'Collection',
        metaBaseUrl: `https://cdn.stealthisnft.xyz/colls/${collectionData.id}/`
      });
    }
  }, [collectionData]);

  if (isLoading) {
    return (
      <main className="main-content mt-1 border-radius-lg">
        <div className="container-fluid text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </main>
    );
  }

  if (!sessionStorageGet('token')) {
    return null;
  }

  return (
    <main className="main-content mt-1 border-radius-lg">
      <Modal show={show} content={content} onClose={() => setShow(false)} />
      <div className="container-fluid">
        <div className="page-header breadcrumb-header min-height-40 border-radius-xl mt-4 mb-30">
          <BreadcrumbSimple text1="Manage Collections: Deploy" />
        </div>
      </div>

      <div className="container-fluid">
        <div className="row">
          {/* Left Column - Form/Instructions */}
          <div className="col-lg-8">
            <div className="container-fluid">
              <div className="card card-purple p-3 mb-3">
                <h6>Deployment Information</h6>
                <p>
                  This page handles the deployment of NFT collections. The actual implementation
                  would include:
                </p>
                <ul>
                  <li>Metadata validation</li>
                  <li>IPNS publishing</li>
                  <li>Contract deployment (if needed)</li>
                  <li>File upload verification</li>
                </ul>

                <div className="mt-3">
                  <strong>Collection Data:</strong>
                  <pre className="bg-dark text-light p-2 mt-2 rounded">
                    {JSON.stringify(collectionData, null, 2)}
                  </pre>
                </div>

                <div className="mt-3">
                  <strong>Files:</strong> {filesData?.length || 0} files ready
                </div>
              </div>

              {/* Placeholder for deployment form */}
              <div className="card card-purple p-3">
                <h6>Metadata Preview</h6>
                <output id="result" />
              </div>
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className="col-lg-4">
            <div className="card card-purple p-3">
              <h6>Preview</h6>
              {metadata && (
                <div className="text-center">
                  <img
                    src={`${metadata.metaBaseUrl}1.png`}
                    alt={metadata.name}
                    className="img-fluid rounded mb-2"
                    style={{ maxHeight: '300px' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div className="text-muted" style={{ display: 'none' }}>
                    No preview available
                  </div>
                  <p className="mt-2">{metadata.name}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default DeployContainer;
